<template>
	<span>
		<Menu />

		<!-- 20260215 -- Copilot (Claude Sonnet 4.5) -- Removed CreateTaskPopup duplicate -->
		<!-- WHY: AppLayout already includes CreateTaskPopup, having it here creates 2 modals with id="taskModal" -->
		<!-- CONTEXT: Discovered via Cypress tests showing 2 #taskModal elements in DOM -->

	<TaskSortPopup :expandDetail="expanded" @setDetail:TaskSortPopup="setExpanded" />

		<Loading
			v-if="$store.getters.getUserLoading"
			:isLoading="$store.getters.getUserLoading"
		/>

		<AppLayout
			v-if="!$store.getters.getUserLoading"
			:title="headerText"
			:subtitle="subheaderText"
			:showDefaultControls="true"
			@toggle:kanban="toggleKanbanFromLayout"
		>
			<!-- Header Controls Slot -->
			<template #controls>
				<b-button-group size="sm">
					<b-button
						v-if="quickNavEnabled"
						id="tasks-button-previous"
						@click="changeOffset(-1)"
						variant="outline-secondary"
					>
						<b-icon icon="caret-left-fill" />
					</b-button>

					<b-button
						id="tasks-button-stats"
						to="/dashboard"
						variant="outline-secondary"
					>
						{{ visibleTasks }}
					</b-button>
					<b-tooltip target="tasks-button-stats" triggers="hover">
						{{ visibleTasks }} tasks displayed
					</b-tooltip>

					<b-button
						id="tasks-button-sort"
						@click="sortTasks()"
						variant="outline-secondary"
					>
						<b-icon-eye />
					</b-button>
					<b-tooltip target="tasks-button-sort" triggers="hover">
						Display Options
					</b-tooltip>

					<b-button
						v-if="quickNavEnabled"
						id="tasks-button-next"
						@click="changeOffset(1)"
						variant="outline-secondary"
					>
						<b-icon icon="caret-right-fill" />
					</b-button>
				</b-button-group>
			</template>

			<!-- Main Content Slot -->
			<template #content>
				<!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Inline filter builder for /tasks/custom -->
				<!-- WHY: Replaces TaskFilterPopup modal; filter builder lives on the Custom screen -->
				<!-- NOTE: filterBuilderVisible must be checked first — getTasksLoading can be true from -->
				<!-- auth-triggered store init while custom route deliberately skipped loadTasks -->
				<div v-if="filterBuilderVisible" class="custom-filter-builder p-4 mb-3">
					<div class="filter-builder-header mb-3">
						<span class="filter-builder-title">Filter</span>
						<b-button
							size="sm"
							variant="link"
							class="filter-help-toggle p-0"
							@click="filterHelpVisible = !filterHelpVisible"
						>
							<b-icon :icon="filterHelpVisible ? 'x-lg' : 'question-circle'" />
						</b-button>
					</div>

					<div v-show="filterHelpVisible" class="filter-help-panel mb-4">
						<p class="mb-2">Each row defines one condition. Conditions are combined by their <strong>Group</strong>:</p>
						<ul class="mb-2">
							<li>Rows in the <strong>same group</strong> (A, B, C…) are joined with <strong>AND</strong> — all must match.</li>
							<li>Rows in <strong>different groups</strong> are joined with <strong>OR</strong> — any group matching is sufficient.</li>
						</ul>
						<p class="mb-2"><strong>Columns:</strong> Group &nbsp;·&nbsp; Field (what to check) &nbsp;·&nbsp; Operator (how to compare) &nbsp;·&nbsp; Value (what to match against)</p>
						<p class="mb-0"><strong>Row controls:</strong>
							<strong>+</strong> Add a row &nbsp;·&nbsp;
							<strong>↺</strong> Clear this row &nbsp;·&nbsp;
							<strong>×</strong> Remove this row
						</p>
					</div>

					<FilterRow
						v-for="(filterItem, idx) in localFilterItems"
						:key="idx"
						:index="idx"
						:filterItem="filterItem"
						class="mb-3"
						@add:filter="addFilterItem"
						@remove:filter="removeFilterItem"
					/>
					<div class="mt-4 d-flex justify-content-end">
						<b-button variant="success" @click="applyCustomFilter()">Apply Filter</b-button>
					</div>
				</div>

				<!-- Inline task loading (replaces full-screen takeover) -->
				<div v-else-if="$store.getters.getTasksLoading" class="task-loading-indicator">
					<b-spinner variant="secondary" />
				</div>

				<template v-else>
					<!-- Modify Filter toggle (custom route, results showing) -->
					<div v-if="$route.params.filterName === 'custom'" class="mb-3">
						<b-button size="sm" variant="outline-secondary" @click="filterBuilderVisible = true">
							<b-icon icon="chevron-right" class="me-1" />
							Modify Filter
						</b-button>
					</div>

					<!-- Empty State -->
					<div
						v-if="
							typeof $store.getters.getTasks == 'undefined' ||
							$store.getters.getTasks === null ||
							$store.getters.getTasks.length == 0
						"
						class="empty-state-container"
					>
						<div class="empty-state-content">
							<div class="empty-state-icon">
								<b-icon-check-circle font-scale="8" />
							</div>
							<h2 class="empty-state-title">All Clear!</h2>
							<p class="empty-state-message">No tasks to display for this view. Time to relax or plan ahead.</p>
							<div class="empty-state-actions mt-4">
							<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Changed to use modal instead of routing -->
							<b-button variant="success" @click="addTask({}, by)" class="me-2">
									<b-icon icon="plus-circle" class="me-2" />
									Create Task
								</b-button>
								<b-button variant="secondary" to="/dashboard">
									<b-icon icon="graph-up" class="me-2" />
									View Dashboard
								</b-button>
							</div>
						</div>
					</div>

					<!-- Tasks Display -->
					<div v-else class="task-list">
						<!-- 20260423 -- Copilot (Claude Sonnet 4.6) -- Add isPremiumUser() gate so lapsed subscribers can't see Kanban via stale pref -->
						<TaskDropZone
							v-if="$store.getters.getUserPrefs.displayKanban && isPremiumUser()"
							:columnField="$store.getters.getUserPrefs.kanbanGroup"
							:tasks="$store.getters.getTasks"
							@message:task="makeToast"
						/>

						<TaskTableRow
							v-else
							v-for="(task, TaskTableIndex) in $store.getters.getTasks"
						:key="task._id"
							:index="TaskTableIndex"
							:task="task"
							:childrenExpanded="expanded"
							:detailExpanded="expanded"
							@createChild:task="addChildTask"
							@delete:task="deleteTask"
							@message:task="makeToast"
						/>
					</div>
				</template>
			</template>
		</AppLayout>
	</span>
</template>
<script>
import _ from 'lodash';
import AppLayout from '@/components/AppLayout.vue'
// 20260215 -- Copilot (Claude Sonnet 4.5) -- Removed CreateTaskPopup import (provided by AppLayout)
import FilterRow from '@/components/FilterRow.vue'
import Loading from '@/components/Loading.vue'
import Menu from '@/components/menus/UserMenu.vue'
import TaskDropZone from '@/components/blocks/TaskDropZone.vue'
import TaskSortPopup from '@/components/TaskSortPopup.vue'
import TaskTableRow from '@/components/TaskTableRow.vue'
// 20260128 -- Copilot (Claude Sonnet 4.5) -- Import TaskService for search filter processing
import TaskService from '@/services/TaskService'
import taskOps from '../mixins/taskOps'
import genericOps from '../mixins/genericOps'

export default {
	name: 'MyTasks',
	metaInfo() {
			return {
				title: 'Principal Task | Tasks',
				meta: []
			}
		},
	components: {
		AppLayout,
		// CreateTaskPopup removed - provided by AppLayout
		FilterRow,
		Loading,
		Menu,
		TaskDropZone,
		TaskSortPopup,
		TaskTableRow,
	},
	mixins: [taskOps, genericOps],
	data() {
		return {
			by: {
				field: null,
				value: null,
			},
			dataForKanban: [],
			taskBeingDragged: null,
			headerText: '',
			subheaderText: '',
			task: {},
			moment: require('moment'),
			message: '',
			quickNavEnabled: false,
			dismissSecs: 5,
			dismissCountDown: 0,
			showDismissibleAlert: false,
		// 20260126 -- Copilot (Claude Sonnet 4.5) -- Default false = unchecked = collapsed
		// WHY: Tasks should be collapsed by default, checkbox controls expansion
		expanded: false,
		expandedIcon: 'arrows-expand',
			sidebar: false,
			offsetCount: 0,
			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Search functionality (moved to AppLayout sidebar)
			// WHY: Allow users to search task titles and notes
			searchTerm: '',		// 20260128 -- Copilot (Claude Sonnet 4.5) -- Flag to prevent route watcher from overriding search
		skipNextMount: false,
		// 20260606 -- Copilot (Claude Sonnet 4.6) -- Inline filter builder state for /tasks/custom
		filterBuilderVisible: false,
		filterHelpVisible: false,
		localFilterItems: [{}],
		}
	},
	beforeRouteUpdate(to, from, next) {
		// console.log('From: ' + JSON.stringify(from.path) + ' -> to: ' + JSON.stringify(to.path))

		this.offsetCount = 0

		// 20201212 -- ZWS -- Clear the filter by (e.g. folder = inbox)
		// so that there's no default when creating tasks
		this.by.field = null
		this.by.value = null

		if (
			to.path.startsWith('/tasks/context') ||
			to.path.startsWith('/tasks/folder') ||
			to.path.startsWith('/tasks/priority') ||
			to.path.startsWith('/tasks/status')
		) {
			this.prepareSimpleFilter(to)
		} else {
			this.prepareFilter(to.params.filterName)
		}

		next()
	},
	mounted() {
		this.offsetCount = 0
		
		// 20260128 -- Copilot (Claude Sonnet 4.5) -- Check for search query parameter
		// WHY: Handle cross-page search navigation
		if (this.$route.query.search) {
			this.handleSidebarSearch(this.$route.query.search, true)
			// Don't clean up here - let route watcher handle it
		} else {
			this.mountPage()
		}
		
		// 20260128 -- Copilot (Claude Sonnet 4.5) -- Listen for sidebar search events
		// WHY: Search moved to AppLayout sidebar for persistent visibility
		this.$root.$on('sidebar:search', this.handleSidebarSearch)
		this.$root.$on('sidebar:clearSearch', this.handleClearSearch)
	},
	beforeUnmount() {
		// Clean up event listeners
		this.$root.$off('sidebar:search', this.handleSidebarSearch)
		this.$root.$off('sidebar:clearSearch', this.handleClearSearch)
	},
	computed: {
		visibleTasks: function() {
			try {
				return this.$store.getters.getTasks.length
			} catch (e) {
				// Intentionally left blank
				return 0
			}
		},
	},
	watch: {
		$route(to, from) {
			// If navigating with search query, handle it
			if (to.query.search) {
				this.$nextTick(() => {
					this.handleSidebarSearch(to.query.search, true)
					// Clean up query param
					this.$router.replace({ path: to.path, query: {} })
				})
			} else if (from && from.query.search && !to.query.search && from.path === to.path) {
				// Just cleaned up the search query param - don't reload
				return
			} else {
				this.mountPage()
			}
		}
	},
	methods: {
		addChildTask(parentTask) {
			// 20260127 -- Copilot (Claude Sonnet 4.5) -- Emit event to AppLayout to prepare child task
			// WHY: Modal uses AppLayout.task, not Tasks.task - need to update parent's task object
			console.log('Tasks.addChildTask - parentTask =', parentTask)
			console.log('Tasks.addChildTask - parentTask._id =', parentTask._id)
			
			if (typeof parentTask !== 'undefined' && parentTask !== null) {
				// Emit to root (AppLayout) to prepare task with parent
				this.$root.$emit('prepare:childTask', parentTask)
			}

			this.$bvModal.show('taskModal')
		},
		// 20260128 -- Copilot (Claude Sonnet 4.5) -- Search functionality
		// WHY: Filter tasks by searching title and notes fields with case-insensitive substring matching
		// UPDATED 20260128: Moved to sidebar, responds to AppLayout search events
		handleSidebarSearch(searchTerm, cleanFilter = false) {
			if (!searchTerm || searchTerm.trim() === '') {
				// Clear search filter and reload tasks
				this.mountPage()
				return
			}
			
			// Store the search term for display/reference
			this.searchTerm = searchTerm.trim()
			
			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Use separate route for search results
			// WHY: Preserve user's custom filter; search is a temporary system-generated filter
			// Build search filter: show incomplete tasks matching search term in title OR notes
			const searchFilter = {
				filterItems: [
					{
						group: 's',
						field: 'completed',
						operator: '$eq',
						value: 'N'
					},
					{
						group: 's',
						field: 'title',
						operator: '$regex',
						value: this.searchTerm
					},
					{
						group: 't',
						field: 'completed',
						operator: '$eq',
						value: 'N'
					},
					{
						group: 't',
						field: 'notes',
						operator: '$regex',
						value: this.searchTerm
					}
				]
			}
			
			// 20260206 -- Copilot (Claude Sonnet 4.5) -- Handle repeated searches on same route
			// WHY: Vue Router won't navigate to same route, must reload directly if already there
			if (this.$route.name === 'tasks-search') {
				// Already on search route - reload with new filter directly
				this.taskFilter = searchFilter
				// 20260606 -- Copilot (Claude Sonnet 4.6) -- Load without persisting to userPrefs
				// WHY: applyFilter → setTaskFilter would overwrite the user's stored custom filter
				TaskService.refineTaskFilter(this.taskFilter, this.quickFilters, 0)
				this.$store.dispatch('loadTasks', this.taskFilter.refinedFilter)
			} else {
				// Navigate to search route with filter in state (doesn't modify userPrefs)
				this.$router.push({
					name: 'tasks-search',
					state: { filter: searchFilter, searchTerm: this.searchTerm }
				})
			}
		},
		handleClearSearch() {
			this.searchTerm = ''
			this.mountPage()
		},
		changeOffset(amount) {
			this.offsetCount = this.offsetCount + amount
			this.mountPage()
		},
		dragTask(details) {
			console.log('TaskCard dragging = ')
			console.log(JSON.stringify(details.event))
			console.log(JSON.stringify(details.task))
			this.taskBeingDragged = details.task
		},
		// async dropTask(event, index) {
		// 	console.log('TaskCard dropped onto list ' + index)
		// 	console.log(JSON.stringify(event))

		// 	this.taskBeingDragged[this.kanbanGroupName] = this.dataForKanban[index].item

		// 	if (this.taskBeingDragged.context == 'Unassigned') {
		// 		this.taskBeingDragged.context = null
		// 	}

		// 	this.saveTaskEdit2(this.taskBeingDragged)
		// },
		
		hideTaskSidebar() {
			this.sidebar = false
		},
		mountPage() {
			
			// 20201212 -- ZWS -- Clear the filter by (e.g. folder = inbox)
			// so that there's no default when creating tasks
			this.by.field = null
			this.by.value = null
			
			if (
				this.$route.path.startsWith('/tasks/context') ||
				this.$route.path.startsWith('/tasks/folder') ||
				this.$route.path.startsWith('/tasks/ps') ||
				this.$route.path.startsWith('/tasks/priority') ||
				this.$route.path.startsWith('/tasks/status')
			) {
				this.prepareSimpleFilter(this.$route)
			} else if (this.$route.path.startsWith('/gtd/tasks/inbox')) {
				
				this.$store.dispatch('loadTasksForGTDInbox');
				this.headerText = 'GTD Inbox'
				this.subheaderText = 'Tasks that without a Status.'

			}
			 else {
				this.prepareFilter(this.$route.params.filterName, this.offsetCount)
			}
		},
		/*
			20191218 -- Removed because it doesn't look necessary any longer.
			countDownChanged(dismissCountDown) {
				// Necessary for the above b-alert
				this.dismissCountDown = dismissCountDown
			},
			*/
		prepareFilter(systemFilterName, offset) {
			////console.log('Tasks.prepareFilter(' + systemFilterName + ')')

			let effectiveOffset = 0
			effectiveOffset = this.offsetCount * 1

			// 20260606 -- Copilot (Claude Sonnet 4.6) -- Reset filter builder visibility on every route change
			// WHY: filterBuilderVisible is instance state; without this reset it bleeds to non-custom routes
			this.filterBuilderVisible = false

			this.taskFilter = this.$store.getters.getUserPrefs.taskFilter

			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Handle search route
			// WHY: Search results use separate route with filter in router state
			if (systemFilterName == 'search') {
				// Get search filter from router state
				const searchFilter = window.history.state?.filter || this.$route.state?.filter
				const searchTerm = window.history.state?.searchTerm || this.$route.state?.searchTerm
				
				if (searchFilter) {
					this.taskFilter = searchFilter
					this.searchTerm = searchTerm || ''
					this.quickNavEnabled = false
					this.headerText = 'Search Results'
					this.subheaderText = searchTerm ? `Tasks matching "${searchTerm}"` : 'Search results'
					// 20260606 -- Copilot (Claude Sonnet 4.6) -- Load without persisting to userPrefs
					// WHY: applyFilter → setTaskFilter would overwrite the user's stored custom filter
					TaskService.refineTaskFilter(this.taskFilter, this.quickFilters, 0)
					this.$store.dispatch('loadTasks', this.taskFilter.refinedFilter)
					return
				}
			}

			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Handle system-filter route (dashboard, header clicks)
			// WHY: System-generated filters shouldn't modify user's custom filter
			if (systemFilterName == 'system-filter') {
				const systemFilter = window.history.state?.filter || this.$route.state?.filter
				
				if (systemFilter) {
					this.taskFilter = systemFilter
					this.quickNavEnabled = false
					this.headerText = 'Filtered Tasks'
					this.subheaderText = 'Tasks matching selected criteria'
					// 20260606 -- Copilot (Claude Sonnet 4.6) -- Load without persisting to userPrefs
					// WHY: applyFilter → setTaskFilter would overwrite the user's stored custom filter
					TaskService.refineTaskFilter(this.taskFilter, this.quickFilters, 0)
					this.$store.dispatch('loadTasks', this.taskFilter.refinedFilter)
					return
				}
			}

			if (systemFilterName == 'get-organized') {
				this.quickNavEnabled = false

				this.taskFilter.quickFilter = 0
				this.headerText = 'Get Organized'
				this.subheaderText = 'Tasks that were to be started or finished previously.'
			} else if (systemFilterName == 'overdue') {
				this.taskFilter.quickFilter = 3

				this.quickNavEnabled = false

				this.headerText = 'Overdue'
				this.subheaderText = 'Tasks that should be complete.'
			} else if (systemFilterName == 'todays-focus') {
				this.taskFilter.quickFilter = 1

				this.quickNavEnabled = true

				if (this.offsetCount == 0) {
					this.headerText = "Today's Focus"
					this.subheaderText = 'Tasks to start or finish today.'
				} else {
					let now = this.moment().startOf('day')
					now.add(effectiveOffset, 'days')

					this.headerText = now.format('ddd, MMM Do')
					this.subheaderText = 'Tasks to start or finish on ' + now.format('MM/DD/YYYY') + '.'
				}
			} else if (systemFilterName == 'this-week') {
				this.taskFilter.quickFilter = 2

				this.quickNavEnabled = true

				if (this.offsetCount == 0) {
					this.headerText = 'This Week'
					this.subheaderText = 'Tasks to start or finish this week.'
				} else {
					// 20260128 -- Copilot (Claude Sonnet 4.5) -- Changed to Sunday start (US standard)
					// WHY: Week filter now starts on Sunday, not Monday
					let now = this.moment().startOf('week')
					now.add(effectiveOffset * 7, 'days')

					this.headerText = 'Week of ' + now.format('MMM Do')
					this.subheaderText = 'Tasks to start or finish the week of ' + now.format('MMM Do') + '.'
				}
			} else if (systemFilterName == 'custom') {
				// 20260606 -- Copilot (Claude Sonnet 4.6) -- Show inline filter builder; user must click Apply to load tasks
				// WHY: Custom screen now owns the filter builder inline; modal (TaskFilterPopup) removed
				const storedFilter = this.$store.getters.getUserPrefs.taskFilter
				this.taskFilter = storedFilter
				this.localFilterItems = storedFilter?.filterItems?.length > 0
					? [...storedFilter.filterItems]
					: [{}]
				this.quickNavEnabled = false
				this.headerText = 'Custom'
				this.subheaderText = 'Results based on your filter.'
				// Populate store with contexts/folders so FilterRow dropdowns have options
				this.$store.dispatch('getContextsForFilter')
				this.$store.dispatch('getFoldersForFilter')
				this.filterBuilderVisible = true
				return
			} else {
				// 20260606 -- Copilot (Claude Sonnet 4.6) -- Separate system-filter (history state) from stored custom filter
				// WHY: The tasks-system-filter named route has no :filterName param so systemFilterName is
				// undefined here; detect it via history state and load transiently without persisting to userPrefs
				const filterData = window.history.state?.filter || this.$route.params.filter

				if (filterData && filterData.filterItems) {
					this.taskFilter = filterData
					this.quickNavEnabled = false
					this.headerText = 'Filtered Tasks'
					this.subheaderText = 'Tasks matching selected criteria'
					TaskService.refineTaskFilter(this.taskFilter, this.quickFilters, 0)
					this.$store.dispatch('loadTasks', this.taskFilter.refinedFilter)
					return
				}

				// No history state filter — apply the user's stored custom filter
				this.taskFilter.quickFilter = null
				this.quickNavEnabled = false
				this.headerText = 'Custom'
				this.subheaderText = 'Results based on your filter.'
			}

			this.applyFilter(this.taskFilter, effectiveOffset)
			},
		prepareSimpleFilter(destinationRoute) {
			// 20200328 -- ZWS -- Introduced to allow filtering by context,
			// folder, priority, status, etc. via links from the Dashboard
			// without having to interrupt the user's defined custom filter.

			// console.log('Tasks.prepareSimpleFilter has route path = ' + destinationRoute.path)
			// console.log('Tasks.prepareSimpleFilter has route params = ' + JSON.stringify(destinationRoute.params))

			let tempFilter = {}
			tempFilter.$or = []

			Object.entries(destinationRoute.params).forEach(([key, value]) => {
				if (key == 'filter') {
					// Move any specific filter criteria to the parent
					// object (tempFilter) so that they can be easily used.

					Object.entries(destinationRoute.params.filter).forEach(([filterKey, filterValue]) => {
						// Override filtering for "unassigned" value
						if (filterValue == 'Unassigned') {
							delete tempFilter[filterKey]

							tempFilter.$or = []

							let fieldA = {}
							fieldA[filterKey] = { $eq: '' }
							tempFilter.$or.push(fieldA)

							let fieldB = {}
							fieldB[filterKey] = null
							tempFilter.$or.push(fieldB)
						} else {
							tempFilter[filterKey] = filterValue
						}
					})
				} else {
					// Simply copy input criteria to the filter

					tempFilter[key] = value

					// Override filtering for "unassigned" value
					if (value == 'Unassigned') {
						delete tempFilter[key]

						tempFilter.$or = []

						let fieldA = {}
						fieldA[key] = { $eq: '' }
						tempFilter.$or.push(fieldA)

						let fieldB = {}
						fieldB[key] = null
						tempFilter.$or.push(fieldB)
					}

					this.headerText = key
					this.headerText = 'By ' + this.headerText.charAt(0).toUpperCase() + this.headerText.slice(1)
					this.subheaderText = 'Tasks assigned to the ' + value + ' ' + key + '.'

					// 20201212 -- ZWS -- Save the filter by (e.g. folder = inbox)
					// so that it can be used when creating tasks
					this.by.field = key
					this.by.value = value
				}
			})

			if (tempFilter.$or.length == 0) {
				delete tempFilter.$or
			}

			// If nothing specifically filters on the completed field, add such a
			// filter.
			if (typeof tempFilter.completed == 'undefined' || tempFilter.completed === null) {
				tempFilter.completed = 'N'
			}

			////console.log('Tasks.prepareSimpleFilter has tempFilter = ' + JSON.stringify(tempFilter))

			this.$store.dispatch('loadTasks', tempFilter)
		},
		// 20260126 -- Copilot (Claude Sonnet 4.5) -- Changed from toggle to set
		// WHY: Checkbox should set the specific value, not toggle
		setExpanded(value) {
		console.log('Tasks.vue setExpanded called with:', value)
		this.expanded = value

		if (this.expanded) {
			this.expandedIcon = 'arrows-collapse'
		} else {
			this.expandedIcon = 'arrows-expand'
		}
	},

	toggleSidebar() {
			// Toggle the kanban display preference
			this.$store.dispatch('toggleKanban')
		},
		toggleKanbanFromLayout(enabled) {
			// Called from AppLayout component
			if (enabled !== this.$store.getters.getUserPrefs.displayKanban) {
				this.$store.dispatch('toggleKanban')
			}
		},
		sortTasks() {
			// Open sort popup
			this.$bvModal.show('taskSortPopupModal')
		},
		// 20260606 -- Copilot (Claude Sonnet 4.6) -- Inline filter builder methods for /tasks/custom
		applyCustomFilter() {
			this.taskFilter.quickFilter = null
			this.taskFilter.filterItems = this.localFilterItems
			this.applyFilter(this.taskFilter)
			this.filterBuilderVisible = false
		},
		addFilterItem(index) {
			this.localFilterItems.splice(index + 1, 0, {})
		},
		removeFilterItem(index) {
			if (this.localFilterItems.length > 1) {
				this.localFilterItems.splice(index, 1)
			}
		},
	},
}
</script>

<style scoped>
.task-loading-indicator {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 200px;
}

.custom-filter-builder {
	border: 1px solid #dee2e6;
	border-radius: 0.375rem;
	background-color: #f8f9fa;
}

.filter-builder-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.filter-builder-title {
	font-weight: 600;
	font-size: 0.95rem;
}

.filter-help-toggle {
	font-size: 0.8rem;
	color: #6c757d;
	text-decoration: none;
}

.filter-help-toggle:hover {
	color: #495057;
}

.filter-help-panel {
	background-color: #e9ecef;
	border-radius: 0.25rem;
	padding: 0.75rem 1rem;
	font-size: 0.85rem;
	color: #495057;
}

.filter-help-panel ul {
	padding-left: 1.25rem;
	margin-bottom: 0;
}


.active {
	color: #78cb5b;
}
.inactive {
	color: #fff000;
}
.fa.fa-circle:before {
	content: '\25C9';
	/*color: #000;*/
}

.empty-state-container {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 500px;
	padding: 3rem 1rem;
}

.empty-state-content {
	text-align: center;
	max-width: 500px;
}

.empty-state-icon {
	margin-bottom: 2rem;
	animation: fadeInScale 0.6s ease-out;
}

.empty-state-icon svg {
	width: 8rem !important;
	height: 8rem !important;
	color: #28a745;
}

.empty-state-title {
	font-size: 2.5rem;
	font-weight: 600;
	color: #28a745;
	margin-bottom: 1rem;
}

.empty-state-message {
	font-size: 1.25rem;
	color: #6c757d;
	margin-bottom: 0;
}

.empty-state-actions {
	display: flex;
	gap: 0.5rem;
	justify-content: center;
	flex-wrap: wrap;
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}
</style>
